import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Stack,
    Checkbox,
    FormControlLabel,
    Autocomplete,
    MenuItem,
    Select,
    CircularProgress as MuiCircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { add_brand, remove_brand, get_folders } from '../../../store/job/jobSlice';
import { isAuth } from '../../../helpers/cookies';
import { Radio, RadioGroup, FormControl, FormLabel } from '@mui/material';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const AddToBrandModal = ({ open, onClose, fileData, jobId: jobIdProp, onStatusChange }) => {
    const getJobIdFromUrl = () => {
        const match = window.location.pathname.match(/\/(?:job-detail|update-job)\/(\d+)/);
        return match ? parseInt(match[1]) : null;
    };
    const jobId = jobIdProp || getJobIdFromUrl();

    const [name, setName] = useState('');
    const [folderError, setFolderError] = useState(false);
    const [searchParam, setSearchParam] = useState('');
    const [isBrand, setIsBrand] = useState(false);
    const [isPersonal, setIsPersonal] = useState(false);
    const [markedForRemoval, setMarkedForRemoval] = useState(false);
    const [loading, setLoading] = useState(false);
    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [newFolderInput, setNewFolderInput] = useState('');
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const dispatch = useDispatch();


    const user = isAuth();
    // Supporting both numeric and string role representations
    const canSaveToBrand = [1, 2, 3, 5].includes(Number(user?.role)) ||
        ['SUPER_ADMIN', 'ADMIN', 'ADDNECTAR_ADMIN', 'COMPANY_MANAGER'].includes(String(user?.role).toUpperCase());

    useEffect(() => {
        if (!open || !fileData) return;

        setName(fileData.logo_name || fileData.name || '');
        setSearchParam(fileData.search_param || fileData.fileName || '');

        if (fileData.is_personal === true) {
            setIsPersonal(true);
            setIsBrand(false);
        } else if (fileData.is_personal === false) {
            setIsBrand(true);
            setIsPersonal(false);
        } else {
            if (!canSaveToBrand) {
                setIsPersonal(true);
                setIsBrand(false);
            } else {
                setIsBrand(false);
                setIsPersonal(false);
            }
        }

        setMarkedForRemoval(false);

    }, [open, fileData, canSaveToBrand]);

    useEffect(() => {
        if (!open) return;

        const fetchFolders = async () => {
            const action = await dispatch(
                get_folders({ is_personal: isPersonal, all_levels: true })
            );

            if (action.payload?.data) {
                const allFolders = action.payload.data;

                // Build a lookup map for O(1) parent resolution
                const folderMap = {};
                allFolders.forEach(f => { folderMap[f.id] = f; });

                // Walk parent_id chain from root → leaf, joined by " >> "
                const buildPath = (folder, visited = new Set()) => {
                    if (!folder) return '';
                    // Guard against circular references (shouldn't happen, but safe)
                    if (visited.has(folder.id)) return folder.name;
                    visited.add(folder.id);

                    if (!folder.parent_id || !folderMap[folder.parent_id]) {
                        // Root-level folder
                        return folder.name;
                    }
                    const parentPath = buildPath(folderMap[folder.parent_id], visited);
                    return `${parentPath} >> ${folder.name}`;
                };

                // Every folder gets its own full-path entry — parents are NOT hidden
                const foldersWithPaths = allFolders
                    .map(f => ({ ...f, fullPath: buildPath(f) }))
                    .sort((a, b) => a.fullPath.localeCompare(b.fullPath));

                setFolders(foldersWithPaths);
            }
        };

        fetchFolders();

    }, [isPersonal, open]);


    const handleSave = async () => {
        if (markedForRemoval) {
            await handleRemove();
            return;
        }

        if (!isBrand && !isPersonal) {
            alert("Please select at least one library type (Brand or Personal)");
            return;
        }
        if (isCreatingNew) {
            if (!newFolderInput || !newFolderInput.trim()) {
                setFolderError(true);
                return;
            }
        }
        setFolderError(false);

        const isNewUpload = fileData?.file instanceof File;
        const hasId = !!fileData?.prooflog_id;
        const hasPath = typeof fileData?.file === 'string';

        if (!hasId && isNewUpload) {
            // New upload case - wait for job save
            if (onStatusChange) {
                onStatusChange(true, {
                    name,
                    search_param: searchParam,
                    is_brand: isBrand,
                    is_personal: isPersonal,
                    folder_id: isCreatingNew ? undefined : selectedFolder?.id,
                    new_folder_name: isCreatingNew ? newFolderInput : undefined
                });
            }
            onClose();
            return;
        }

        // Ensure we have either prooflog_id or file_path for the backend
        if (!hasId && !hasPath) {
            alert("Cannot add to library: file has no path or proof log ID.");
            return;
        }

        setLoading(true);
        try {
            // Clean the file path - strip full URL to relative path
            let cleanFilePath = hasPath ? fileData.file : undefined;
            if (cleanFilePath && typeof cleanFilePath === 'string') {
                // Remove the Linode base URL prefix if present
                cleanFilePath = cleanFilePath.replace(/^https?:\/\/[^/]+\.linodeobjects\.com\//, '');
                cleanFilePath = decodeURIComponent(cleanFilePath);
            }

            console.log('[AddToBrandModal] Saving with payload:', {
                job_id: jobId,
                prooflog_id: fileData.prooflog_id,
                file_path: cleanFilePath,
                name,
                isBrand,
                isPersonal,
                hasId,
                hasPath,
            });

            if (isBrand) {
                await dispatch(add_brand({
                    job_id: parseInt(jobId),
                    prooflog_id: fileData.prooflog_id || undefined,
                    file_path: cleanFilePath,
                    name: name,
                    search_param: searchParam,
                    is_personal: false,
                    folder_id: isCreatingNew ? undefined : selectedFolder?.id,
                    new_folder_name: isCreatingNew ? newFolderInput : undefined
                })).unwrap();
            }

            if (isPersonal) {
                await dispatch(add_brand({
                    job_id: parseInt(jobId),
                    prooflog_id: fileData.prooflog_id || undefined,
                    file_path: cleanFilePath,
                    name: name,
                    search_param: searchParam,
                    is_personal: true,
                    folder_id: isCreatingNew ? undefined : selectedFolder?.id,
                    new_folder_name: isCreatingNew ? newFolderInput : undefined
                })).unwrap();
            }

            onClose();
            if (onStatusChange) onStatusChange(true, { name, search_param: searchParam, is_brand: isBrand, is_personal: isPersonal });

        } catch (error) {
            console.error("Failed to add to library", error);
            // Alert removed as requested by user since it often shows even when successful

        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async () => {
        if (!fileData?.prooflog_id) {
            // New upload case
            if (onStatusChange) onStatusChange(false, null);
            onClose();
            return;
        }

        setLoading(true);
        try {
            await dispatch(remove_brand(fileData.prooflog_id)).unwrap();
            if (onStatusChange) onStatusChange(false, null);
            onClose();
        } catch (error) {
            console.warn("Remove library warning:", error);
            if (onStatusChange) onStatusChange(false, null);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Save to Library</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Typography variant="body2" color="text.secondary" mb={2}>
                    File: {fileData?.fileName}
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Library Type
                    </Typography>
                    <FormControl>
                        <RadioGroup
                            row
                            value={isBrand ? 'brand' : isPersonal ? 'personal' : ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                setIsBrand(val === 'brand');
                                setIsPersonal(val === 'personal');
                            }}
                        >
                            {canSaveToBrand && (
                                <FormControlLabel
                                    value="brand"
                                    control={<Radio size="small" />}
                                    label={<Typography variant="body2">Brand Library</Typography>}
                                />
                            )}

                            <FormControlLabel
                                value="personal"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">My Library</Typography>}
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                <TextField
                    fullWidth
                    label="Logo Name"
                    variant="outlined"
                    size="small"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={markedForRemoval}
                />

                <TextField
                    fullWidth
                    label="Search Parameters (Tags)"
                    variant="outlined"
                    size="small"
                    multiline
                    rows={2}
                    value={searchParam}
                    onChange={(e) => setSearchParam(e.target.value)}
                    placeholder="e.g. logo, primary, dark background"
                    sx={{ mb: 2 }}
                    disabled={markedForRemoval}
                />

                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">
                            Folder (Optional)
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={isCreatingNew}
                                    onChange={(e) => setIsCreatingNew(e.target.checked)}
                                    disabled={markedForRemoval}
                                    sx={{ py: 0 }}
                                />
                            }
                            label={<Typography variant="caption">Create Folder</Typography>}
                            sx={{ m: 0 }}
                        />
                    </Stack>

                    {isCreatingNew ? (
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter new folder name"
                            value={newFolderInput}
                            onChange={(e) => {
                                setNewFolderInput(e.target.value);
                                if (folderError) setFolderError(false);
                            }}
                            error={folderError}
                            helperText={folderError ? "Folder name is required" : ""}
                            disabled={markedForRemoval}
                        />
                    ) : (
                        <Autocomplete
                            size="small"
                            options={folders}
                            getOptionLabel={(option) => option.fullPath || option.name}
                            value={selectedFolder}
                            onChange={(e, val) => setSelectedFolder(val)}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Select folder" />
                            )}
                            disabled={markedForRemoval}
                        />
                    )}
                </Box>


                {fileData?.inBrand && (
                    <Box sx={{ mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={markedForRemoval}
                                    onChange={(e) => setMarkedForRemoval(e.target.checked)}
                                    color="error"
                                />
                            }
                            label={
                                <Typography variant="body2" color={markedForRemoval ? 'error' : 'textPrimary'}>
                                    Remove from Brand library
                                </Typography>
                            }
                        />
                    </Box>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button variant="outlined" onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading || (isPersonal === null && !markedForRemoval)}
                        sx={{ bgcolor: markedForRemoval ? '#d32f2f' : '#ff6100', '&:hover': { bgcolor: markedForRemoval ? '#b71c1c' : '#e65700' } }}
                    >
                        {loading ? <MuiCircularProgress size={24} color="inherit" /> : (markedForRemoval ? 'Remove' : 'Save')}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};

export default AddToBrandModal;
